import { useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../lib/supabase';

type ZipSuggestion = { postal_code: string; city: string; state: string };

type UseZipLookupResult = {
  suggestions: ZipSuggestion[];
  resolvedLocation: string | null;
  isLoading: boolean;
};

const cache = new Map<string, ZipSuggestion[]>();

export function useZipLookup(zipInput: string): UseZipLookupResult {
  const [suggestions, setSuggestions] = useState<ZipSuggestion[]>([]);
  const [resolvedLocation, setResolvedLocation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const digits = zipInput.replace(/\D/g, '');

    // Reset when input is too short
    if (digits.length < 3) {
      setSuggestions([]);
      setResolvedLocation(null);
      setIsLoading(false);
      clearTimeout(timerRef.current);
      return;
    }

    // At exactly 5 digits, resolve city/state
    if (digits.length === 5) {
      setSuggestions([]);
      setIsLoading(true);
      clearTimeout(timerRef.current);

      const key = `resolve:${digits}`;
      if (cache.has(key)) {
        const hit = cache.get(key)!;
        setResolvedLocation(hit.length > 0 ? `${hit[0].city}, ${hit[0].state}` : null);
        setIsLoading(false);
        return;
      }

      let active = true;
      getSupabaseClient()
        .from('postal_code_timezones')
        .select('postal_code, city, state')
        .eq('country', 'US')
        .eq('postal_code', digits)
        .limit(1)
        .then(({ data }) => {
          if (!active) return;
          const rows = (data ?? []) as ZipSuggestion[];
          cache.set(key, rows);
          setResolvedLocation(rows.length > 0 ? `${rows[0].city}, ${rows[0].state}` : null);
          setIsLoading(false);
        });

      return () => { active = false; };
    }

    // 3-4 digits: show suggestions after debounce
    setResolvedLocation(null);
    setIsLoading(true);
    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const prefix = digits;
      const key = `suggest:${prefix}`;

      if (cache.has(key)) {
        setSuggestions(cache.get(key)!);
        setIsLoading(false);
        return;
      }

      let active = true;
      getSupabaseClient()
        .from('postal_code_timezones')
        .select('postal_code, city, state')
        .eq('country', 'US')
        .like('postal_code', `${prefix}%`)
        .order('postal_code')
        .limit(8)
        .then(({ data }) => {
          if (!active) return;
          const rows = (data ?? []) as ZipSuggestion[];
          cache.set(key, rows);
          setSuggestions(rows);
          setIsLoading(false);
        });

      // Cleanup for the async query inside the timeout
      // We can't cancel the Supabase query, but we can ignore stale results
      return () => { active = false; };
    }, 250);

    return () => { clearTimeout(timerRef.current); };
  }, [zipInput]);

  return { suggestions, resolvedLocation, isLoading };
}
